import { useState, useEffect } from 'react'
import { getPublicStorageUrl } from '@/utils'

/**
 * Processes evidence data into a format ready for video display
 * @param evidence Array of evidence items
 * @param videoUrls Object mapping meeting IDs to their video URLs
 * @param meetings Array of meeting objects
 * @returns Array of processed video objects ready for display
 */
export function useProcessedVideos(evidence: any[], videoUrls: Record<string, string>, meetings: any[]) {
  const [processedVideos, setProcessedVideos] = useState<any[]>([])

  useEffect(() => {
    if (!evidence?.length || Object.keys(videoUrls).length === 0) {
      setProcessedVideos([])
      return
    }

    // Create a meetings lookup map for faster access
    const meetingsMap = meetings.reduce(
      (acc, meeting) => {
        if (meeting.id) acc[meeting.id] = meeting
        return acc
      },
      {} as Record<string, any>
    )

    // Only process evidence that has associated videos
    const videos = evidence
      .filter((note) => {
        const meeting = meetingsMap[note.meeting_id]
        return meeting && videoUrls[note.meeting_id]
      })
      .map((note) => {
        const meeting = meetingsMap[note.meeting_id]
        const url = videoUrls[note.meeting_id]

        // Check if this is an audio file based on meeting type or other indicators
        const isAudio = determineIfAudio(meeting)

        // Safely handle missing thumbnails
        let thumbnail = ''
        try {
          // Check for 'thumbnail' field which is the correct field used in the backend
          if (meeting.thumbnail) {
            // Only convert to absolute URL if it's not already absolute
            if (meeting.thumbnail.startsWith('http')) {
              thumbnail = meeting.thumbnail
            } else {
              thumbnail = getPublicStorageUrl('thumbs', meeting.thumbnail)
            }
          }
          // Fallback to check for 'stored_thumbnail' field (legacy data might use this)
          else if (meeting.stored_thumbnail) {
            // Only convert to absolute URL if it's not already absolute
            if (meeting.stored_thumbnail.startsWith('http')) {
              thumbnail = meeting.stored_thumbnail
            } else {
              thumbnail = getPublicStorageUrl('thumbs', meeting.stored_thumbnail)
            }
          }
          // No thumbnail found
          else {
            console.warn(`No thumbnail found for meeting ${meeting.id}`)
          }
        } catch (error) {
          console.error(`Failed to get thumbnail for meeting ${meeting.id}:`, error)
        }

        return {
          id: note.note_ID || `note-${meeting.id}-${note.start_time}`,
          url,
          thumbnail,
          startTime: note.start_time,
          endTime: note.end_time,
          title: note.title || meeting.name || 'Untitled Video',
          meetingId: note.meeting_id,
          isAudio, // Add flag for audio files
        }
      })

    // Remove duplicates (same video URL, start and end time)
    const uniqueVideos = []
    const seen = new Set()

    for (const video of videos) {
      const key = `${video.url}-${video.startTime}-${video.endTime}`
      if (!seen.has(key)) {
        seen.add(key)
        uniqueVideos.push(video)
      }
    }

    setProcessedVideos(uniqueVideos)
  }, [evidence, videoUrls, meetings])

  return processedVideos
}

/**
 * Determines if a meeting contains audio-only content rather than video
 * @param meeting The meeting object to check
 * @returns boolean True if the meeting is an audio file, false otherwise
 */
function determineIfAudio(meeting: any): boolean {
  // Check the file type/mime type if available
  if (meeting.type && typeof meeting.type === 'string') {
    if (meeting.type.startsWith('audio/')) {
      return true
    }
    // If it's explicitly a video type, it's not audio-only
    if (meeting.type.startsWith('video/')) {
      return false
    }
  }

  // Check if meeting has an audio_only flag
  if (meeting.audio_only === true) {
    return true
  }

  // Check the URL for audio file extensions
  if (meeting.meeting_url && typeof meeting.meeting_url === 'string') {
    const url = meeting.meeting_url.toLowerCase()
    if (
      url.endsWith('.mp3') ||
      url.endsWith('.wav') ||
      url.endsWith('.aac') ||
      url.endsWith('.ogg') ||
      url.endsWith('.m4a')
    ) {
      return true
    }
  }

  // Check the stored file name for audio extensions
  if (meeting.stored_name && typeof meeting.stored_name === 'string') {
    const filename = meeting.stored_name.toLowerCase()
    if (
      filename.endsWith('.mp3') ||
      filename.endsWith('.wav') ||
      filename.endsWith('.aac') ||
      filename.endsWith('.ogg') ||
      filename.endsWith('.m4a')
    ) {
      return true
    }
  }

  // Default to false - assume it's a video unless we have reason to believe it's audio
  return false
}
